import { useState, useEffect } from 'react';
import { dataStore } from '../services/dataStore';
import { Material, CutJob } from '../types';
import { AIJobOptimizer } from './AIJobOptimizer';
import { CodexJobOptimization } from '../services/codexService';

interface CutJobFormProps {
  onBack: () => void;
  onJobCreated: (job: CutJob) => void;
}

export function CutJobForm({ onBack, onJobCreated }: CutJobFormProps) {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [measurementSystem, setMeasurementSystem] = useState<'imperial' | 'metric'>('imperial');
  const [feet, setFeet] = useState('');
  const [inches, setInches] = useState('');
  const [meters, setMeters] = useState('');
  const [lengthFeet, setLengthFeet] = useState(0); // normalized to feet for pricing
  const [quantity, setQuantity] = useState('1');
  const [calculatedCost, setCalculatedCost] = useState(0);
  // AI Optimizer is now always visible; no toggle state

  useEffect(() => {
    const allMaterials = dataStore.getMaterials();
    setMaterials(allMaterials);
    if (allMaterials.length > 0) setSelectedMaterialId(allMaterials[0].id);
  }, []);

  // normalize inputs to feet
  useEffect(() => {
    if (measurementSystem === 'imperial') {
      const f = parseFloat(feet || '0');
      const i = parseFloat(inches || '0');
      setLengthFeet(f + i / 12);
    } else {
      const m = parseFloat(meters || '0');
      setLengthFeet(m * 3.28084);
    }
  }, [feet, inches, meters, measurementSystem]);

  // price calculation
  useEffect(() => {
    const mat = materials.find(m => m.id === selectedMaterialId);
    const qty = parseInt(quantity || '0');
    if (!mat || !lengthFeet || !qty || isNaN(lengthFeet) || isNaN(qty)) {
      setCalculatedCost(0);
      return;
    }
    const materialCost = mat.unitCost * lengthFeet * qty;
    const labor = qty * 0.25; // 25 cents per cut
    const waste = materialCost * 0.15; // 15%
    const subtotal = materialCost + labor + waste;
    const markup = subtotal * 0.25; // 25%
    setCalculatedCost(subtotal + markup);
  }, [materials, selectedMaterialId, lengthFeet, quantity]);

  const handleOptimizationApplied = (optimization: CodexJobOptimization) => {
    // apply suggested length (in feet)
    setLengthFeet(optimization.suggestedLength);
    alert('AI optimization applied. Suggested length: ' + optimization.suggestedLength + ' ft. Estimated savings: ' + optimization.costSavings);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mat = materials.find(m => m.id === selectedMaterialId);
    const qty = parseInt(quantity || '0');
    if (!customerName.trim() || !mat || !lengthFeet || !qty) {
      alert('Please complete all fields with valid values');
      return;
    }
    const totalNeededFeet = lengthFeet * qty;
    if (totalNeededFeet > mat.currentStock) {
      alert('Insufficient stock. Available: ' + mat.currentStock + 'ft, Needed: ' + totalNeededFeet.toFixed(2) + 'ft');
      return;
    }

    const job: CutJob = {
      id: Date.now().toString(),
      customerName: customerName.trim(),
      material: mat,
      length: lengthFeet,
      quantity: qty,
      totalCost: calculatedCost,
      laborCost: qty * 0.25,
      wasteCost: mat.unitCost * lengthFeet * qty * 0.15,
      status: 'pending',
      createdAt: new Date(),
      orderCode: Math.random().toString(36).slice(2, 6).toUpperCase(),
    };

    dataStore.updateMaterialStock(mat.id, mat.currentStock - totalNeededFeet);
    dataStore.saveJob(job);
    onJobCreated(job);
  };

  const selectedMaterial = materials.find(m => m.id === selectedMaterialId);

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b-2 border-black p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="btn-base btn-primary">⬅️ Back</button>
            <div>
              <h1 className="text-2xl font-bold text-black">CREATE CUT JOB</h1>
              <p className="text-base text-gray-600">Generate a new cutting order</p>
            </div>
          </div>
          <span className="text-2xl">✂️</span>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 min-h-0 p-4">
        <div className="h-full grid grid-cols-1 lg:grid-cols-2 gap-2 gap-compact">
          {/* Form */}
          <div className="card-base p-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xl">👤</span>
              <h2 className="text-2xl font-semibold text-black">JOB DETAILS</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-base font-semibold block mb-2">Customer Name</label>
                <input className="input-base" placeholder="Enter customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
              </div>

              <div>
                <label className="text-base font-semibold block mb-2">Material</label>
                <select className="input-base" value={selectedMaterialId} onChange={(e) => setSelectedMaterialId(e.target.value)}>
                  {materials.map(m => (
                    <option key={m.id} value={m.id}>{m.name} - {'$'}{m.unitCost}/ft (Stock: {m.currentStock}ft)</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-base font-semibold block mb-2">Measurement System</label>
                <div className="flex gap-3">
                  <button type="button" className={`btn-base ${measurementSystem === 'imperial' ? 'btn-mint' : 'btn-primary'}`} onClick={() => setMeasurementSystem('imperial')}>Imperial (ft/in)</button>
                  <button type="button" className={`btn-base ${measurementSystem === 'metric' ? 'btn-mint' : 'btn-primary'}`} onClick={() => setMeasurementSystem('metric')}>Metric (m)</button>
                </div>
              </div>

              {measurementSystem === 'imperial' ? (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-base font-semibold block mb-2">Feet</label>
                    <input className="input-base" type="number" min="0" value={feet} onChange={(e) => setFeet(e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <label className="text-base font-semibold block mb-2">Inches</label>
                    <input className="input-base" type="number" min="0" max="11.99" step="0.01" value={inches} onChange={(e) => setInches(e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <label className="text-base font-semibold block mb-2">Quantity</label>
                    <input className="input-base" type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="1" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-base font-semibold block mb-2">Length (meters)</label>
                    <input className="input-base" type="number" step="0.01" min="0.01" value={meters} onChange={(e) => setMeters(e.target.value)} placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-base font-semibold block mb-2">Quantity</label>
                    <input className="input-base" type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="1" />
                  </div>
                </div>
              )}

              <div className="card-base p-3">
                <div className="text-center text-lg font-bold">Total: {'$'}{calculatedCost.toFixed(2)}</div>
              </div>

              {/* Cost Breakdown (compact) */}
              {selectedMaterial && (parseInt(quantity || '0') > 0) && (lengthFeet > 0) && (
                <div className="card-base p-3">
                  {(() => {
                    const unit = selectedMaterial.unitCost;
                    const qtyN = parseInt(quantity || '0') || 0;
                    const matCost = unit * lengthFeet * qtyN;
                    const labor = qtyN * 0.25;
                    const waste = matCost * 0.15;
                    const subtotal = matCost + labor + waste;
                    const markup = subtotal * 0.25;
                    return (
                      <ul className="text-sm space-y-1">
                        <li className="flex justify-between"><span>Material</span><span>{'$'}{matCost.toFixed(2)}</span></li>
                        <li className="flex justify-between"><span>Labor (0.25/cut)</span><span>{'$'}{labor.toFixed(2)}</span></li>
                        <li className="flex justify-between"><span>Waste (15%)</span><span>{'$'}{waste.toFixed(2)}</span></li>
                        <li className="flex justify-between"><span>Markup (25%)</span><span>{'$'}{markup.toFixed(2)}</span></li>
                        <li className="flex justify-between font-semibold pt-1"><span>Total</span><span>{'$'}{(subtotal + markup).toFixed(2)}</span></li>
                      </ul>
                    );
                  })()}
                </div>
              )}

              <button type="submit" className="btn-base btn-coral w-full font-bold">Create Job</button>
            </form>

            {/* AI Optimizer is always visible in the right column */}
          </div>

          <div className="card-base p-4 overflow-auto max-h-full">
            <AIJobOptimizer
              material={selectedMaterial ? selectedMaterial.name : ''}
              length={lengthFeet || 0}
              quantity={(parseInt(quantity || '0') || 0)}
              customerName={customerName}
              materialUnitCost={selectedMaterial ? selectedMaterial.unitCost : undefined}
              currentStock={selectedMaterial ? selectedMaterial.currentStock : undefined}
              onOptimizationApplied={handleOptimizationApplied}
            />
          </div>
        </div>
      </div>
    </div>
  );
}